import { Sparkles } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { PasswordInput } from "@/components/common";
import { Button, Input } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import { gtmService } from "@/services/gtm-service";

import { GoogleAuthButton } from "./components/GoogleAuthButton";
import { useAuthRedirect } from "./hooks/useAuthRedirect";
import { useSignIn } from "./hooks/useSignIn";

export const SignInForm = () => {
  const location = useLocation();
  const { redirectAfterAuth } = useAuthRedirect();

  const onSuccess = () => {
    redirectAfterAuth();
  };

  const {
    email,
    setEmail,
    password,
    setPassword,
    handleSubmit,
    isLoading,
    isValid,
    error,
  } = useSignIn({ onSuccess });

  useEffect(() => {
    gtmService.trackLogInView();
  }, []);

  return (
    <div className="w-full max-w-md">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-lg shadow-primary/25">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-bold text-3xl text-zinc-900">Welcome back</h1>
        <p className="mt-2 text-zinc-500">Sign in to your Dualio account</p>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-destructive bg-destructive/10 px-4 py-3 text-destructive text-sm">
          {error}
        </div>
      )}

      <GoogleAuthButton mode="login" onSuccess={onSuccess} />

      <div className="my-5 flex items-center gap-3">
        <hr className="flex-1 border-zinc-200" />
        <span className="text-xs text-zinc-400">or</span>
        <hr className="flex-1 border-zinc-200" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <label className="block font-medium text-sm text-zinc-700">Email</label>
          <Input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="block font-medium text-sm text-zinc-700">Password</label>
          </div>
          <PasswordInput
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
        </div>

        <Button
          type="submit"
          disabled={isLoading || !isValid}
          className="h-12 w-full text-base"
        >
          {isLoading ? "Logging in..." : "Log in"}
        </Button>

        <div className="text-center">
          <span className="text-sm text-zinc-500">Don't have an account? </span>
          <Link
            to={ROUTES.SIGNUP}
            state={location.state}
            className="font-semibold text-primary text-sm no-underline hover:text-primary-hover"
          >
            Sign up
          </Link>
        </div>

        <p className="text-center text-xs text-zinc-400">
          By logging in, you agree to our{" "}
          <Link to={ROUTES.TERMS} className="text-primary hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to={ROUTES.PRIVACY} className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>
      </form>
    </div>
  );
};
